<!-- @format -->
<template>
	<div class="pcp-header3-my-systems">
		<div
			v-if="state.leftClickable"
			:key="'left'"
			class="pcp-header3-my-systems-left"
			:style="{ cursor: state.leftClickable ? 'pointer' : 'default' }"
			@click="onClickLeft"
		>
			<el-icon><ArrowLeft /></el-icon>
		</div>
		<div ref="refSystemsWrap" class="pcp-out-systems" @scroll="handleScroll">
			<div
				v-for="item in globalStore.subSystems"
				:key="item.id"
				:class="{ 'active-name': globalStore.curSystemAlias === item.alias }"
				class="pcp-systems-name"
				@click="() => toSubSystem(item)"
			>
				<div class="div">
					<img v-if="item.logo" :src="item.logo" class="img" />
					<div>{{ item.name }}</div>
				</div>
			</div>
		</div>
		<div
			v-if="state.rightClickable"
			:key="'right'"
			class="pcp-header3-my-systems-right"
			:style="{ cursor: state.rightClickable ? 'pointer' : 'default' }"
			@click="onClickRight"
		>
			<el-icon><ArrowRight /></el-icon>
		</div>
	</div>
</template>

<script lang="ts" setup>
	import { useGlobalStoreWithOut } from '@power/runtime-vue3';
	import { toSubSystem } from './utils';

	const globalStore = useGlobalStoreWithOut();

	const refSystemsWrap = ref<any>(null);

	// 响应式状态
	const state = reactive({
		leftClickable: false, // 左侧箭头是否可点击
		rightClickable: false, // 右侧箭头是否可点击
	});

	// 计算每个菜单项的宽度
	const getMenuItemWidth = () => {
		const menuItems = refSystemsWrap.value?.querySelectorAll('.pcp-systems-name');
		return menuItems && menuItems.length > 0 ? menuItems[0].offsetWidth : 200; // 默认200px
	};

	// 处理滚动事件
	const handleScroll = () => {
		if (!refSystemsWrap.value) return;

		const scrollLeft = refSystemsWrap.value.scrollLeft;
		const scrollWidth = refSystemsWrap.value.scrollWidth;
		const clientWidth = refSystemsWrap.value.clientWidth;

		// 当滚动条在最左侧时，隐藏左侧箭头
		state.leftClickable = scrollLeft > 0;

		// 当滚动条在最右侧时，隐藏右侧箭头
		state.rightClickable = scrollLeft < scrollWidth - clientWidth - 4;
	};

	const onClickLeft = () => {
		if (!refSystemsWrap.value) return;

		const itemWidth = getMenuItemWidth();
		const targetScroll = Math.max(0, refSystemsWrap.value.scrollLeft - itemWidth * 2);

		refSystemsWrap.value.scrollTo({
			left: targetScroll,
			behavior: 'smooth',
		});
	};

	const onClickRight = () => {
		if (!refSystemsWrap.value) return;

		const itemWidth = getMenuItemWidth();
		const maxScroll = refSystemsWrap.value.scrollWidth - refSystemsWrap.value.clientWidth;
		const targetScroll = Math.min(maxScroll, refSystemsWrap.value.scrollLeft + itemWidth * 2);

		refSystemsWrap.value.scrollTo({
			left: targetScroll,
			behavior: 'smooth',
		});
	};
	// 滚动到选中的菜单项
	const scrollToSelectedItem = (item) => {
		if (!refSystemsWrap.value) return;

		const menuItems = refSystemsWrap.value.querySelectorAll('.pcp-systems-name');
		const targetItem: any = Array.from(menuItems).find((el: any) => {
			const nameEl = el.querySelector('div div');
			return nameEl && nameEl.textContent.trim() === item.name.trim();
		});

		if (targetItem) {
			// 计算目标位置
			const targetScroll = targetItem.offsetLeft - (refSystemsWrap.value.clientWidth - targetItem.offsetWidth + 150);

			// 滚动到目标位置
			refSystemsWrap.value.scrollTo({
				left: Math.max(0, Math.min(targetScroll, refSystemsWrap.value.scrollWidth - refSystemsWrap.value.clientWidth)),
				behavior: 'smooth',
			});
		}
	};

	// 监听当前系统变化，更新滚动位置
	watch(
		() => globalStore.curSystemAlias,
		(newAlias) => {
			const currentSystem = globalStore.subSystems.find((s) => s.alias === newAlias);
			if (currentSystem) {
				scrollToSelectedItem(currentSystem);
			}
		},
	);

	onMounted(() => {
		handleScroll();
		// 初始滚动到当前选中的菜单项
		const currentSystem = globalStore.subSystems.find((s) => s.alias === globalStore.curSystemAlias);
		if (currentSystem) {
			setTimeout(() => {
				scrollToSelectedItem(currentSystem);
			}, 100);
		}
	});
</script>

<style lang="scss" scoped>
	@import '../../assets/styles/themes/pcp-header.scss';

	.pcp-header3-my-systems {
		display: flex;
		max-width: 980px;
		align-items: center;

		&-left,
		&-right {
			color: #fff;
			width: 45px;
			padding: 10px 15px;
			margin-top: 3px;
			font-size: 14px;
		}
	}

	.pcp-out-systems {
		display: flex;
		max-width: 880px;
		white-space: nowrap;
		overflow-x: auto;
		color: #edf2ff;

		&::-webkit-scrollbar {
			width: 0px;
			height: 0px;
		}

		.active-name {
			background: rgba(0, 115, 255, 1) !important;
			border: 1px solid rgba(255, 255, 255, 0.8) !important;
			cursor: pointer;
		}

		.pcp-systems-name {
			padding: 3px 12px;
			background: rgba(0, 115, 255, 0.2);
			border: 1px solid rgba(255, 255, 255, 0.2);
			margin: 0 10px;
			background-size: 100% 100%;
			font-size: 14px;
			height: 30px;

			.div {
				display: flex;
				align-items: center;
				height: 100%;
			}

			img {
				width: 15px;
				height: 16px;
				background-size: 100% 100%;
				margin-right: 5px;
			}

			&:hover {
				background: rgba(0, 115, 255, 0.5);
				border: 1px solid rgba(255, 255, 255, 0.5);
				cursor: pointer;
			}
		}
	}
</style>
